import { signInWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import apiClient from '@/services/api/apiClient';
import { firebaseAuth } from '@/services/firebase';
import { AdminConfigService } from '@/services/instances/admin-config';
import { getConfigFromStorage } from '@/utils/config';
import { TwoFactorResponseData } from '../../model/auth';

/**
 * Autenticação do admin web — agora via **Firebase Auth** + 2FA por email.
 *
 * Fluxo:
 * 1. `login(email, senha)` → Firebase valida a senha (signIn) e dispara o
 *    desafio 2FA (`POST /dashboard-user/2fa/start`, com o ID token no Bearer).
 * 2. `verifyTwoFactor(code)` → valida o código e devolve `{ twofa_session, name, uuid }`.
 *
 * O ID token do Firebase é gerido pelo SDK (renova sozinho) e anexado pelo
 * interceptor; a sessão 2FA vai no header `x-2fa-session`.
 */
export class AuthRepository {
  /** Passo 1: senha no Firebase + dispara o código 2FA por email. Lança em falha. */
  static async login(email: string, password: string): Promise<void> {
    // Garante o client_id no storage antes do 2fa/start — no boot da página o
    // fetch do /admin-config/config ainda pode estar em andamento (race).
    if (!getConfigFromStorage()?.client_id) {
      await AdminConfigService.setConfig();
    }

    await signInWithEmailAndPassword(firebaseAuth, email, password);
    const [err] = await apiClient.post<void>('/api/v1/dashboard-user/2fa/start');
    if (err) {
      throw err;
    }
  }

  /** Passo 2: valida o código 2FA → retorna a sessão. */
  static verifyTwoFactor(code: string) {
    return apiClient.post<TwoFactorResponseData>('/api/v1/dashboard-user/2fa/verify', { code });
  }

  /** Esqueci minha senha → email de redefinição do Firebase. Lança em falha. */
  static forgotPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(firebaseAuth, email);
  }

  /** Logout: encerra a sessão do Firebase. */
  static logout(): Promise<void> {
    return signOut(firebaseAuth);
  }
}
